import { Popup } from '@progress/kendo-react-popup'
import AlertPrompt from 'components/AlertPrompt'
import AddAttendee from 'components/AlertPrompt/AddAttendee'
import CreateNewClient from 'components/AlertPrompt/CreateNewClient'
import { days_of_week } from 'components/Modals/Class/ClassItem'
import {
    GroupClassApi,
    GroupClassAvailabilityApi,
    ModalDataAttributes,
    RecurrenceSchedule,
} from 'components/Modals/types'
import Translation from 'components/Translation'
import {
    useAppContext,
} from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import Link from 'next/link'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { useBookingService } from 'services/booking'
import { updateGroupClassAvailability } from 'services/group-class'
import { normalizeUser } from 'services/user'
import { AttendeeItem } from 'types/attendee'
import { BookingForm, BookingFormExtra } from 'types/booking'
import { CalendarSlotItem } from 'types/calendar'
import { UpdateGroupClassAvailabilityInput } from 'types/group-class'
import { ServiceApiTypeEnum } from 'types/service'
import { TenantIntegration } from 'types/tenant-integration'
import { classNames } from 'utils/dom-helpers'
import { patchApiRequest, putApiRequest, useApiRequest } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import {
    addDurationToStr,
    toHHMM,
    toHHMM12,
    toYmd,
} from 'utils/string-localization-helper'
import { GViewItemExtraProps } from './GViewItem'
import { Menu } from '@headlessui/react'
import { GEditItem } from './GEditItem'
import { AttendeeListItem } from './AttendeeListItem'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import ViewResponse, { ViewResponseStatus } from 'components/Modals/IntakeForms/ViewResponse'
import { IntakeClientResponse, IntakeForm, IntakeResponseDTO, mapIntakeFormFromDTO, mapIntakeResponseFromDTO } from 'types/intake-forms'

export type ItemSidebarProps = {
    show: boolean
    toggleShow(show: boolean): void
    anim_css: string[]
    setAnimCss(anim_css: string[]): void
    data: CalendarSlotItem
    setData: Dispatch<SetStateAction<CalendarSlotItem>>
    staff: Record<string, string> | undefined
    setStaff(staff: Record<string, string> | undefined): void
    payment_integrations: TenantIntegration[] | undefined
    attendees: AttendeeItem[]
    setAttendees: Dispatch<SetStateAction<AttendeeItem[]>>
    booking_form: BookingForm & BookingFormExtra
    setBookingForm(booking_form: BookingForm & BookingFormExtra): void
    is_from_client_modal: boolean
    main_attendee_idx?: number
}

export default function ItemSidebar(
    props: ItemSidebarProps & GViewItemExtraProps
) {
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const {
        getGroupClassAvailabilityBookings,
        getGroupClassAvailabilityAttendees,
    } = useBookingService()
    const { locale, translations, setGlobalLoading, is_mobile } = useAppContext()
    const { services, locations, tenant } = useTenantContext()
    const {
        show,
        toggleShow,
        anim_css,
        setAnimCss,
        data,
        setData,
        staff,
        setStaff,
        payment_integrations,
        attendees,
        setAttendees,
        booking_form,
        setBookingForm,
        is_from_client_modal,
        main_attendee_idx,
    } = props

    const [current_data, setCurrentData] = useState<CalendarSlotItem>()
    const [recurring_data, setRecurringData] = useState<
        GroupClassApi | undefined
    >()
    const [is_cancelling, setCancelling] = useState<boolean>(false)
    const [is_updating, toggleUpdating] = useState<boolean>(false)
    const [is_save_disabled, toggleSaveDisabled] = useState<boolean>(false)
    const [prompt_cancellation, setCancellationPrompt] =
        useState<boolean>(false)
    const [promptAppointmentCancel, setPromptAppointmentCancel] =
        useState(false)
    const [prompt_deletion, promptDelete] = useState<boolean>(false)
    const [prompt_deletion_series, promptDeleteSeries] =
        useState<boolean>(false)
    const [added_attendee_name, notifyAttendeeAddedSuccessfully] =
        useState<string>()
    const [updated_attendee_name, notifyAttendeeUpdatedSuccessfully] =
        useState<string>()
    const [updated_successfully, notifyUpdatedSuccessfully] =
        useState<boolean>()
    const [queue_cancel_booking, promptCancelBooking] = useState<string>()
    const [selectedAttendee, setSelectedAttendee] = useState('')
    const [create_new_client, toggleCreateClientWindow] =
        useState<boolean>(false)
    const [add_existing_client, toggleAddExistingClientWindow] =
        useState<boolean>(false)
    const [item_to_expand, expandBookingAttendeeItem] = useState<number>(
        main_attendee_idx || -1
    )
    const [isDisplayConfirmationModal, setIsDisplayConfirmationModal] =
        useState(false)
    const [is_refetch_on_close, toggleRefetchOnClose] =
        useState<boolean>(false)
    const [show_responses, toggleShowResponses] = useState<ViewResponseStatus>({ show: false })
    const [view_form_index, setViewFormIndex] = useState<number>(0)
    const [client_responses, setClientResponses] = useState<IntakeClientResponse[]>([])
    const [intake_forms, setIntakeForms] = useState<IntakeForm[]>([])
    const [client_ids_with_responses, setClientIdsWithResponses] = useState<string[]>([]) //TODO: optimize

    const isAppointment = !!data?.appointment_id

    const onSuccessfulBooking = useCallback(
        async (
            booking: any
        ) => {
            toggleCreateClientWindow(false)
            toggleAddExistingClientWindow(false)

            if (data?.availability_id) {
                const d = await getGroupClassAvailabilityAttendees(
                    `${data.availability_id}`
                )
                anim_css[1] = ''
                setAnimCss(anim_css)
                if (!is_from_client_modal) {
                    setStaff(normalizeUser(d.instructor.user))
                }

                const bookings_output =
                    await getGroupClassAvailabilityBookings(
                        `${data.availability_id}`
                    )
                if (bookings_output.message) {
                    setGlobalError({
                        message: bookings_output.message,
                        tag: bookings_output?.tag,
                    })
                    return
                }

                if (!is_from_client_modal) {
                    const denorm_attendees: AttendeeItem[] =
                        d.attendees.map((attendee: AttendeeItem) => {
                            const [booking] =
                                bookings_output.bookings.filter(
                                    (booking: Record<string, string>) => {
                                        return (
                                            attendee.booking_id ==
                                            booking.booking_id
                                        )
                                    }
                                )
                            return {
                                ...attendee,
                                order_id: booking.order_id,
                                notes: booking?.notes,
                                signed_in_at: booking?.signed_in_at,
                            }
                        })

                    setAttendees(denorm_attendees)
                }
            } else if (data?.appointment_id) {
                const temp = booking as any

                const newAttendee = {
                    id: temp?.appointmentData?.clientData?.id,
                    first_name:
                        temp?.appointmentData?.clientData?.user?.first_name,
                    last_name: temp?.appointmentData?.clientData?.user?.last_name,
                    user: {
                        id: temp?.appointmentData?.clientData?.user?.id,
                        firstName:
                            temp?.appointmentData?.clientData?.user?.first_name,
                        lastName:
                            temp?.appointmentData?.clientData?.user?.last_name,
                    },
                    order: { paid: true },
                }

                setAttendees((prev) => [
                    ...prev,
                    { ...(newAttendee as any) },
                ])
            }
            if (!is_from_client_modal) {
                setData((prev: CalendarSlotItem) => {
                    return {
                        ...prev,
                        slots: {
                            ...prev.slots,
                            taken: prev.slots.taken + 1,
                            available: prev.slots.available - 1,
                        },
                    }
                })
            }
            anim_css[0] = 'bg-opacity-70'
            setAnimCss(anim_css)
            if (booking?.client?.user) {
                const { first_name, last_name } = booking.client.user as Record<string,string>
                notifyAttendeeAddedSuccessfully(
                    [first_name, last_name].join(' ')
                )
            }
            toggleRefetchOnClose(true)
        },
        [props]
    )

    const handleDelete = useCallback(
        (evt) => {
            if (data && props.handleDelete) {
                props.handleDelete(data)
            }
        },
        [props]
    )

    function onClickEdit(event: MouseEvent) {
        setCurrentData(data)
        props.handleEdit && props.handleEdit(data)
    }

    function cancelEdit() {
        setCancellationPrompt(true)
    }

    async function updateClassSchedule() {
        try {
            toggleUpdating(true)
            setGlobalLoading(true, true)
            if (current_data && current_data?.availability_id) {
                const start_ymd = toYmd(current_data?.start)
                const start_hms = toHHMM(
                    current_data?.startTime || current_data.start
                )
                const gc_updates: UpdateGroupClassAvailabilityInput = {
                    id: current_data.availability_id,
                    date: start_ymd,
                    groupClassId: current_data.id,
                    setting: {
                        groupClassId: current_data.id,
                        staffId: current_data.staff_id,
                        locationId: current_data.location_id,
                        locationSpaceId: current_data.location_space_id,
                    },
                    startTime: start_hms,
                }
                const res = await updateGroupClassAvailability(gc_updates)
                if (res?.id) {
                    notifyUpdatedSuccessfully(true)
                } else throw res
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            toggleUpdating(false)
            setGlobalLoading(false, true)
        }
    }

    useEffect(() => {
        if (
            current_data?.service_type === ServiceApiTypeEnum.GROUP ||
            current_data?.service_type === ServiceApiTypeEnum.SERIES
        )
            toggleSaveDisabled(
                !current_data ||
                !current_data.location_id ||
                !current_data.start ||
                !current_data.staff_id ||
                !current_data.location_space_id ||
                !current_data.startTime
            )
    }, [current_data])

    useEffect(() => {
        if (payment_integrations && !is_from_client_modal) {
            payment_integrations.forEach(
                (integration: TenantIntegration) => {
                    if (!booking_form.connected_account_id) {
                        booking_form.connected_account_id =
                            integration.connected_account_id
                        setBookingForm({
                            ...booking_form,
                            connected_account_id:
                                integration.connected_account_id,
                        })
                        return
                    }
                }
            )
        }
    }, [payment_integrations])

    useEffect(() => {
        if (main_attendee_idx != undefined) {
            expandBookingAttendeeItem(main_attendee_idx)
        }
    }, [main_attendee_idx])

    const generateAppointmentPayload = () => {
        if (current_data) {
            const start_hms = toHHMM12(
                current_data?.startTime || current_data?.start
            )

            let payload = {
                startTime: start_hms,
                serviceId: current_data.service_id,
                endTime: toHHMM12(current_data.end),
                date: toYmd(current_data?.start as Date),
                staffId: current_data.staff_id,
                locationSpaceId: current_data?.location_space_id,
                locationId: current_data.location_id,
                attendees: attendees?.map((item) => item?.user?.id),
            }

            return payload
        }
    }

    const isClassRecurring = async () =>
        await getApiRequest(`/v1/group_classes/${data?.id}`)

    const checkRecurrence = async () => {
        try {
            setGlobalLoading(true, true)
            if (data?.service_type === ServiceApiTypeEnum.GROUP) {
                const check = await isClassRecurring()

                if (check) {
                    if (check?.recurring) {
                        setRecurringData(check)
                        setIsDisplayConfirmationModal(true)
                    } else {
                        await updateSingleClass()
                    }
                }
            } else if (!!data?.appointment_id) {
                const payload = generateAppointmentPayload()
                const res = await putApiRequest(
                    `/v1/appointments/${data.appointment_id}`,
                    payload
                )

                if (res?.ok) {
                    setIsDisplayConfirmationModal(false)
                    notifyUpdatedSuccessfully(true)
                } else {
                    throw res
                }
            } else if (data?.service_type === ServiceApiTypeEnum.SERIES) {
                const availabilities = await getApiRequest(
                    `/v1/group_class_availabilities/group-class?groupClassId=${current_data?.id}`
                )
                const duration = services?.find(
                    (service) => service.id == current_data?.service_id
                )?.duration
                if (availabilities && duration) {
                    const payload: GroupClassApi = {
                        id: current_data?.id as string,
                        name: current_data?.group_class_name as string,
                        serviceId: current_data?.service_id as string,
                        groupClassAvailabilities: (
                            availabilities as ModalDataAttributes[]
                        )?.map((availability: any) => {
                            if (
                                availability.id !=
                                current_data?.availability_id
                            ) {
                                return availability
                            } else {
                                const time = toHHMM(
                                    current_data?.startTime as Date
                                )
                                return {
                                    ...availability,
                                    id: availability?.id,
                                    date: toYmd(
                                        current_data?.start as Date
                                    ),
                                    startTime: time,
                                    endTime: addDurationToStr(
                                        current_data?.start as Date,
                                        time,
                                        duration as number
                                    ),
                                } as GroupClassAvailabilityApi
                            }
                        }),
                        groupClassSetting: {
                            id: current_data?.setting_id as string,
                            locationId: current_data?.location_id as string,
                            locationSpaceId:
                                current_data?.location_space_id as string,
                            staffId: current_data?.staff_id as string,
                        },
                    }
                    const res = await putApiRequest(
                        '/v1/group_classes/series',
                        payload
                    )
                    if (res?.ok) {
                        setIsDisplayConfirmationModal(false)
                        notifyUpdatedSuccessfully(true)
                    } else {
                        throw res
                    }
                }
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            setGlobalLoading(false, true)
        }
    }

    const updateSingleClass = async () => {
        await updateClassSchedule()
        setIsDisplayConfirmationModal(false)
    }

    const transformPayload = (current_data: any) => {
        let payload: any = {}
        if (current_data && current_data.availability_id) {
            const start_hms = toHHMM(
                current_data?.startTime || current_data?.start
            )

            const group_class = {
                id: current_data.id,
                effectiveDate: toYmd(current_data?.start as Date),
                startTime: start_hms,
                expiryDate: recurring_data?.expiryDate,
                recurring: true,
                serviceId: current_data?.service_id as string,
                groupClassSetting: {
                    locationId: current_data?.location_id as string,
                    staffId: current_data?.staff_id as string,
                    locationSpaceId:
                        current_data?.location_space_id as string,
                },
                recurrenceSchedule:
                    (recurring_data?.recurrenceSchedule as any) || 'DAILY',

                // duration: 60,
                dayOfWeek:
                    days_of_week[
                    new Date(
                        toYmd(current_data?.start as Date)
                    ).getDay()
                    ],
            }

            payload = { ...group_class }
            if (payload.recurrenceSchedule === RecurrenceSchedule.DAILY) {
                delete payload?.dayOfWeek
            }
        }

        return payload
    }

    const updateAllClasses = async () => {
        try {
            toggleUpdating(true)
            setGlobalLoading(true, true)
            const payload = transformPayload(current_data)
            const res = await putApiRequest(
                `/v1/group_classes/${current_data?.id}`,
                payload
            )

            if (res?.ok) {
                setIsDisplayConfirmationModal(false)
                notifyUpdatedSuccessfully(true)
            } else {
                throw res
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            toggleUpdating(false)
            setGlobalLoading(false, true)
        }
    }

    const cancelAllClasses = async () => {
        try {
            setGlobalLoading(true, true)
            const payload = transformPayload(data)
            const res = await putApiRequest(
                `/v1/group_classes/soft-delete/${payload?.id}`,
                payload
            )
            if (res?.ok) {
                promptDelete(false)
                notifyUpdatedSuccessfully(true)
            } else {
                throw res
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            setGlobalLoading(false, true)
        }
    }

    const handleDeleteAppointment = async () => {
        try {
            setCancelling(true)
            setGlobalLoading(true, true)
            const res = await patchApiRequest(
                `/v1/bookings/cancel/${data.id}`
            )

            if (res?.ok) {
                setPromptAppointmentCancel(false)
                notifyUpdatedSuccessfully(true)
            } else {
                throw res
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            setCancelling(false)
            setGlobalLoading(false, true)
        }
    }

    const handleDeleteSeries = async () => {
        try {
            setGlobalLoading(true, true)
            const res = await putApiRequest(
                `/v1/group_classes/series/soft-delete/${data.id}`
            )
            if (res?.ok) {
                notifyUpdatedSuccessfully(true)
            } else {
                throw res
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            setGlobalLoading(false, true)
        }
    }

    const closeGView = () => {
        setAnimCss(['bg-opacity-0', 'translate-x-full'])
        expandBookingAttendeeItem(-1)
        setTimeout(() => {
            toggleShow(false)
            if (is_refetch_on_close) {
                props.onUpdate && props.onUpdate()
                toggleRefetchOnClose(false)
            }
        }, 300)
        if (add_existing_client) {
            toggleAddExistingClientWindow(false)
        }
    }

    const handleCancelClass = async () => {
        const isRecurring = await isClassRecurring()
        if (isRecurring?.recurring) {
            setRecurringData(isRecurring)
        }
        promptDelete(true)
    }

    const isRecurring = recurring_data ? recurring_data?.recurring : false

    useEffect(() => {
        if (data?.service_id && data.location_id && show) {
            fetchIntakeForms(data.service_id, data.location_id)
        }
    }, [data?.service_id, data?.location_id, show])

    async function fetchIntakeForms(service_id: string, location_id: string) {
        try {
            let res = await getApiRequest(`/v1/intake-forms/by-location-id-and-service-id?locationId=${location_id}&serviceId=${service_id}`)
            if (res?.length > 0) {
                setIntakeForms(res.map(mapIntakeFormFromDTO))
            }
        } catch (error) {
            setGlobalError(error)
        }
    }

    useEffect(() => {
        fetchClientsWithResponses()
    }, [attendees])

    async function fetchClientsWithResponses() {
        if (attendees?.length > 0) {
            try {
                let res = await getApiRequest(`/v1/intake-forms/responses/get-client-ids-by-order-ids?orderIds=${attendees.map((attendee) => attendee.order?.id).join(',')}`)
                if (res?.length > 0) {
                    setClientIdsWithResponses(res)
                }
            } catch (error) {
                setGlobalError(error)
            }
        } else {
            setClientIdsWithResponses([])
        }
    }

    function handleEditChange(key: string, value: string | number | Date) {
        if (key == 'max_attendees') {
            setCurrentData({
                ...current_data,
                slots: {
                    ...current_data?.slots,
                    available: value as number,
                }
            } as CalendarSlotItem)
        } else if (key == 'staff') {
            setCurrentData({
                ...current_data,
                staff_id: value as string,
            } as CalendarSlotItem)
        } else if (key == 'location_id') {
            const new_location = locations.find(
                (location) => location.id == value
            )
            setCurrentData({
                ...current_data,
                location_id: value as string,
                location_space_id:
                    new_location?.location_spaces?.[0]
                        .id,
            } as CalendarSlotItem)
        } else {
            setCurrentData({
                ...current_data,
                [key]: value,
            } as CalendarSlotItem)
        }
    }

    return (
        <Popup
            show={show}
            popupAlign={{
                horizontal: 'right',
                vertical: 'top',
            }}
            animate={false}
            popupClass={classNames(
                '!bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-between',
                anim_css[0]
            )}
            style={{
                width: '100%',
            }}
            key="item_sidebar"
        >

            <div className="flex-1">
                <ViewResponse
                    status={show_responses}
                    toggleStatus={toggleShowResponses}
                    form={intake_forms[view_form_index]}
                    response={client_responses[view_form_index]}
                    view_form_index={view_form_index}
                    setViewFormIndex={setViewFormIndex}
                    total_forms_count={intake_forms.length}
                    show_client_details={true}
                />
                <CreateNewClient
                    show={create_new_client}
                    data={booking_form}
                    onSuccess={onSuccessfulBooking}
                    title="add_and_book_attendee"
                    toggleShow={() => {
                        toggleCreateClientWindow(!create_new_client)
                    }}
                />{' '}
                <AddAttendee
                    booking_data={booking_form}
                    show={add_existing_client}
                    title="add_attendee"
                    onSuccess={onSuccessfulBooking}
                    onToggleShow={(show: boolean) => {
                        toggleAddExistingClientWindow(show)
                    }}
                    closeAndOpenNewClient={() => {
                        toggleAddExistingClientWindow(false)
                        toggleCreateClientWindow(true)
                    }}
                    isAppointment={isAppointment}
                    appointmentData={isAppointment ? data : undefined}
                />
                <AlertPrompt
                    show={prompt_cancellation}
                    title="discard_changes_title"
                    body="discard_changes_body"
                    actions={[
                        <button
                            type="button"
                            key="cancel"
                            className="border border-gray-200 rounded-lg h-12 text-gray-500 grow"
                            onClick={() => {
                                setCancellationPrompt(false)
                            }}
                        >
                            <Translation content_key="continue_editing" />
                        </button>,
                        <button
                            type="button"
                            key="confirm"
                            className="bg-primary rounded-lg w-lg text-white h-12 grow"
                            onClick={(evt) => {
                                setCurrentData(undefined)
                                setRecurringData(undefined)
                                setCancellationPrompt(false)
                            }}
                        >
                            <Translation content_key="discard_and_exit" />
                        </button>,
                    ]}
                ></AlertPrompt>
                <AlertPrompt
                    show={prompt_deletion}
                    title={
                        isRecurring
                            ? 'Cancel the schedule for this class, or this and all future classes?'
                            : 'Are you sure to cancel the schedule for this class?'
                    }
                    icon="alert-triangle bg-red-500"
                    body={
                        <>
                            {isRecurring && (
                                <span
                                    className="absolute top-1 right-2 cursor-pointer text-lg"
                                    onClick={() => promptDelete(false)}
                                >
                                    x
                                </span>
                            )}
                            <p className="text-red-500 text-lg">
                                {isRecurring && (
                                    <Translation
                                        content_key={
                                            'Warning: Canceling all future classes means any changes made into single classes in the future will be overwritten'
                                        }
                                    />
                                )}
                            </p>
                            <p className=" mt-9 text-base">
                                {isRecurring && (
                                    <Translation
                                        content_key={
                                            translations[
                                            'edit_class_question'
                                            ] ||
                                            'How should the schedule be cancelled?'
                                        }
                                    />
                                )}
                            </p>
                        </>
                    }
                    actions={[
                        <button
                            onClick={() => promptDelete(false)}
                            key={'cancel_signle'}
                            className={`${!isRecurring ? '' : 'hidden'
                                } border border-gray-200 rounded-lg h-12 text-gray-500 grow`}
                        >
                            <Translation content_key="no" />
                            &nbsp;,
                            <Translation content_key="go_back" />
                        </button>,
                        <button
                            type="button"
                            key="cancel"
                            className="border border-gray-200 rounded-lg h-12 text-white grow bg-primary"
                            onClick={(evt) => {
                                // promptDelete(false)
                                handleDelete(evt)
                                setCurrentData(undefined)
                                setRecurringData(undefined)
                                setCancellationPrompt(false)
                            }}
                        >
                            <Translation content_key="cancel" />
                            {isRecurring && (
                                <Translation
                                    content_key={
                                        translations['single_class'] ||
                                        ' This Class Only'
                                    }
                                />
                            )}
                        </button>,
                        <button
                            type="button"
                            key="confirm"
                            className={`bg-primary rounded-lg w-lg text-white h-12 grow w-1/2 px-5 ${isRecurring ? '' : 'hidden'
                                }`}
                            onClick={(evt) => {
                                cancelAllClasses()
                                // handleDelete(evt)
                                // setCurrentData(undefined)
                                // setCancellationPrompt(false)
                            }}
                        >
                            <Translation content_key="delete" /> &nbsp;
                            <Translation
                                content_key={
                                    translations['all_classes'] ||
                                    'This class and ALL future classes'
                                }
                            />
                        </button>,
                    ]}
                />
                <AlertPrompt
                    show={prompt_deletion_series}
                    title={getTranslation(
                        'calendar.delete_series_title',
                        translations
                    )}
                    icon="alert-triangle bg-red-500"
                    body={
                        <>
                            <span
                                className="absolute top-1 right-2 cursor-pointer text-lg"
                                onClick={() => promptDeleteSeries(false)}
                            >
                                x
                            </span>
                            <p className="text-red-500 text-lg">
                                <Translation content_key="calendar.delete_series_note" />
                            </p>
                        </>
                    }
                    actions={[
                        <button
                            type="button"
                            key="cancel"
                            className="border border-gray-200 rounded-lg h-12 text-gray-500 grow"
                            onClick={(evt) => {
                                promptDeleteSeries(false)
                            }}
                        >
                            <Translation content_key="cancel" />
                        </button>,
                        <button
                            type="button"
                            key="confirm"
                            className="bg-primary rounded-lg w-lg text-white h-12 grow w-1/2 px-5"
                            onClick={(evt) => {
                                promptDeleteSeries(false)
                                handleDeleteSeries()
                            }}
                        >
                            <Translation content_key="delete" />
                        </button>,
                    ]}
                />
                {/* edit class */}
                <AlertPrompt
                    show={isDisplayConfirmationModal}
                    title={
                        translations['edit_class_heading'] ||
                        'Edit the schedule for this class, or this and all future classes?'
                    }
                    icon="alert-triangle bg-yellow-500"
                    body={
                        <div>
                            <span
                                className="absolute right-4 top-2 text-base cursor-pointer"
                                onClick={() =>
                                    setIsDisplayConfirmationModal(false)
                                }
                            >
                                x
                            </span>
                            <p className="text-red-500 text-lg">
                                <Translation
                                    content_key={
                                        translations[
                                        'edit_class_sub_heading'
                                        ] ||
                                        'Warning: Editing all future classes means any changes made into single classes in the future will be overwritten'
                                    }
                                />
                            </p>
                            <p className=" mt-9 text-base">
                                <Translation
                                    content_key={
                                        translations[
                                        'edit_class_question'
                                        ] ||
                                        'How should the schedule be updated?'
                                    }
                                />
                            </p>
                        </div>
                    }
                    actions={[
                        <div
                            className="flex w-full"
                            key="class edit options"
                        >
                            <button
                                type="button"
                                className="bg-white rounded-lg w-lg border-2 border-gray-300 h-12 grow w-1/2 mr-2"
                                onClick={updateSingleClass}
                            >
                                <Translation
                                    content_key={
                                        translations['single_class'] ||
                                        ' This Class Only'
                                    }
                                />
                            </button>
                            <button
                                type="button"
                                key="confirm"
                                className="bg-primary rounded-lg w-lg text-white h-12 grow w-1/2 px-5"
                                onClick={updateAllClasses}
                            >
                                <Translation
                                    content_key={
                                        translations['all_classes'] ||
                                        'This class and ALL future classes'
                                    }
                                />
                            </button>
                        </div>,
                    ]}
                />
                <AlertPrompt
                    show={!!updated_successfully}
                    title={
                        data?.service_type === ServiceApiTypeEnum.GROUP ||
                            data?.service_type === ServiceApiTypeEnum.SERIES
                            ? 'successfully_updated_class'
                            : 'appointment_updated'
                    }
                    icon="check bg-green-500"
                    actions={[
                        <button
                            type="button"
                            key="confirm"
                            className="bg-primary rounded-lg w-lg text-white h-12 grow"
                            onClick={(evt) => {
                                notifyUpdatedSuccessfully(false)
                                setRecurringData(undefined)
                                setCurrentData(undefined)
                                setAnimCss([
                                    'bg-opacity-0',
                                    'translate-x-full',
                                ])
                                setTimeout(() => {
                                    toggleShow(false)

                                    updated_successfully != undefined &&
                                        props.onUpdate &&
                                        props.onUpdate()
                                }, 300)
                            }}
                        >
                            <Translation content_key="back_to_appointment" />
                        </button>,
                    ]}
                />
                <AlertPrompt
                    show={
                        added_attendee_name != undefined ||
                        updated_attendee_name != undefined
                    }
                    title={
                        added_attendee_name != undefined
                            ? 'successfully_added_attendee'
                            : 'calendar.successfully_updated_attendee'
                    }
                    icon="check bg-green-500"
                    actions={[
                        <button
                            type="button"
                            key="confirm"
                            className="bg-primary rounded-lg w-lg text-white h-12 grow"
                            onClick={(evt) => {
                                if (added_attendee_name != undefined) {
                                    notifyAttendeeAddedSuccessfully(
                                        undefined
                                    )
                                } else {
                                    notifyAttendeeUpdatedSuccessfully(
                                        undefined
                                    )
                                }
                                if (!is_from_client_modal) {
                                    toggleRefetchOnClose(true)
                                }
                            }}
                        >
                            <Translation content_key="back_to_appointment" />
                        </button>,
                    ]}
                >
                    <div className="rounded-full inline py-2 px-4 bg-green-100">
                        {added_attendee_name || updated_attendee_name}
                    </div>
                </AlertPrompt>
                <AlertPrompt
                    show={promptAppointmentCancel}
                    title="cancel_appointment_title"
                    icon="alert-triangle bg-red-500"
                    body={
                        <p>
                            {translations['cancel_appointment_message'] ||
                                'Are you sure you want to cancel this appointment?'}
                        </p>
                    }
                    actions={[
                        <button
                            key={'no'}
                            className="px-7 py-3 border-2 border-gray-200 rounded-md"
                            onClick={() =>
                                setPromptAppointmentCancel(false)
                            }
                        >
                            <Translation content_key="no" />, &nbsp;
                            <Translation content_key="go_back" />
                        </button>,
                        <button
                            className={`text-white  rounded-md border-2 ${is_cancelling
                                ? 'border-gray-200 bg-gray-200 cursor-not-allowed'
                                : 'bg-primary order-primary'
                                }  b px-7 py-3 focus:ring-0`}
                            key="cancel appoinemnt"
                            onClick={handleDeleteAppointment}
                        >
                            <Translation content_key="yes" />, &nbsp;
                            <Translation content_key="cancel" />
                        </button>,
                    ]}
                />
                <AlertPrompt
                    show={!!queue_cancel_booking}
                    title="cancel_class_booking_title"
                    icon="alert-triangle bg-red-500"
                    actions={[
                        <button
                            type="button"
                            key="cancel"
                            className="border border-gray-200 rounded-lg h-12 text-gray-500 w-1/2 -ml-2"
                            onClick={() => {
                                promptCancelBooking('')
                            }}
                        >
                            <Translation content_key="continue_editing" />
                        </button>,
                        <button
                            type="button"
                            key="confirm"
                            disabled={is_cancelling}
                            className="bg-primary rounded-lg w-1/2 -mr-2 text-white h-12 grow disabled:bg-opacity-30 flex gap-3 items-center justify-center"
                            onClick={async (evt) => {
                                try {
                                    setGlobalLoading(true, true)
                                    if (data?.availability_id) {
                                        setCancelling(true)
                                        const res = await putApiRequest(
                                            `/v1/bookings/cancel-from-list?ids=${queue_cancel_booking}`
                                        )
                                        if (res?.ok != false) {
                                            expandBookingAttendeeItem(-1)
                                            if (!is_from_client_modal) {
                                                const d =
                                                    await getGroupClassAvailabilityAttendees(
                                                        data.availability_id
                                                    )
                                                setAttendees(d?.attendees)
                                                setData((prev) => {
                                                    return {
                                                        ...prev,
                                                        slots: {
                                                            ...prev.slots,
                                                            taken:
                                                                prev.slots
                                                                    .taken - 1,
                                                            available:
                                                                prev.slots
                                                                    .available +
                                                                1,
                                                        },
                                                    }
                                                })
                                                toggleRefetchOnClose(true)
                                            } else {
                                                props.onUpdate &&
                                                    props.onUpdate()
                                                closeGView()
                                            }
                                        }
                                        else {
                                            throw res
                                        }
                                    }
                                    if (data?.appointment_id) {
                                        const params = [
                                            `id=${queue_cancel_booking}`,
                                            `attendeeId=${selectedAttendee as string
                                            }`,
                                        ]

                                        const res = await putApiRequest(
                                            `/v1/appointments/cancel-attendee?${params.join(
                                                '&'
                                            )}`
                                        )

                                        if (res?.ok) {
                                            if (!is_from_client_modal) {
                                                setAttendees((prev) => [
                                                    ...prev.filter((item) =>
                                                        res?.attendees?.includes(
                                                            item?.user
                                                                ?.id as string
                                                        )
                                                    ),
                                                ])
                                                setData((prev) => {
                                                    return {
                                                        ...prev,
                                                        slots: {
                                                            ...prev.slots,
                                                            taken:
                                                                prev.slots
                                                                    .taken -
                                                                1,
                                                            available:
                                                                prev.slots
                                                                    .available +
                                                                1,
                                                        },
                                                    }
                                                })
                                            } else {
                                                props.onUpdate &&
                                                    props.onUpdate()
                                                closeGView()
                                            }
                                        } else {
                                            throw res
                                        }
                                    }
                                    promptCancelBooking('')
                                } catch (error) {
                                    setGlobalError(error)
                                } finally {
                                    setCancelling(false)
                                    setGlobalLoading(false, true)
                                }
                            }}
                        >
                            {is_cancelling ? (
                                <>
                                    <svg
                                        role="status"
                                        className="w-4 h-4 mr-1 text-gray-200 animate-spin dark:text-white fill-primary-light"
                                        viewBox="0 0 100 101"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentFill"
                                        />
                                    </svg>
                                    <Translation content_key="loading" />
                                </>
                            ) : (
                                <Translation content_key="cancel_appointment" />
                            )}
                        </button>,
                    ]}
                />
            </div>
            <div
                className={classNames(
                    'bg-white w-road h-screen overflow-x-hidden flex flex-col transition ease-in-out duration-200 appointment-popup',
                    anim_css[1]
                )}
            >
                <div className='appointment-popup-scroll'>
                <div className="px-6 py-4">
                    <div className="flex justify-between">
                        <div className="flex flex-row grow sm:grow-0 text-xl font-display text-primary mt-4 items-center app-popup-title">
                            <span className='grow sm:grow-0 '>{data?.service_name}</span>
                            <Translation
                                content_key={
                                    'common.type_' + data?.service_type?.toLowerCase()
                                }
                                render_as="span"
                                className="primary-badge"
                            />
                            <Link
                                href={{
                                    pathname: '/book',
                                    query: {
                                        t: tenant?.id,
                                        lang: locale,
                                        l: data?.location_id,
                                        id: data?.id,
                                        d: toYmd(data?.start),
                                    },
                                }}
                            >
                                <a
                                    className="feather-link text-md text-primary-light bg-primary-light bg-opacity-10 p-2 rounded-full mx-2 text-center inline-flex items-center justify-center"
                                    target={'_blank'}
                                />
                            </Link>
                        </div>
                        <button
                            type="button"
                            onClick={closeGView}
                        >
                            <i className="feather-x text-lg" />
                        </button>
                    </div>
                    <div className="flex gap-6 app-popup-profile">
                        <div className="flex flex-wrap text-center w-10 h-10 content-center rounded-full bg-primary-lighter p-2 block overflow-hidden">
                            {staff?.image_url ? (
                                <img
                                    src={staff.image_url}
                                    alt="Staff Photo"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="flex-1 font-black text-primary">
                                    {[
                                        staff?.first_name?.[0],
                                        staff?.last_name?.[0],
                                    ].join('.')}
                                </div>
                            )}
                        </div>
                        <ul className='app-profile-ul'>                        
                            <li>
                                <Translation
                                    render_as="span"
                                    className=" tracking-wider"
                                    content_key="instructor"
                                    translations={translations}
                                />

                                {staff ? (
                                    <Translation
                                        render_as="span"
                                        content_key="locale_fullname"
                                        content_values={{
                                            first_name: staff?.first_name,
                                            last_name: staff?.last_name,
                                        }}
                                        translations={translations}
                                    />
                                ) : (
                                    ''
                                )}
                            </li>
                            <li>
                                <Translation
                                    render_as="span"
                                    className=" tracking-wider"
                                    content_key="location"
                                    translations={translations}
                                />

                                <span>
                                    {data?.online_meeting_url ? (
                                        <a
                                            href={data?.online_meeting_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-hidden="true"
                                            className="location-link"
                                        >
                                            <Translation content_key='common.zoom_link' />
                                            <span className="feather feather-external-link text-primary-light ml-2"></span>
                                        </a>
                                    ) : (
                                        <>{data?.location_name}</>
                                    )}
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="flex flex-col sm:flex-row items-left sm:items-center justify-between font-semibold text-gray-500 text-sm tracking-wider uppercase app-profile-more">
                        <div className="leading-none flex gap-2">
                            <i className="feather-calendar text-primary-light text-md" />
                            <span>
                                {data?.start?.toLocaleDateString(
                                    undefined,
                                    {
                                        dateStyle: 'long',
                                    }
                                )}
                            </span>
                        </div>

                        <div className="leading-none flex gap-2">
                            <i className="feather-clock text-primary-light text-md" />
                            <span>
                                {data?.start
                                    ?.toLocaleTimeString(undefined, {
                                        timeStyle: 'short',
                                    })
                                    .split(' ')
                                    .join('')}
                                -
                                {data?.end
                                    ?.toLocaleTimeString(undefined, {
                                        timeStyle: 'short',
                                    })
                                    .split(' ')
                                    .join('')}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-left sm:items-center justify-between font-semibold text-gray-500 text-sm tracking-wider uppercase app-profile-more">

                        <div className="leading-none flex gap-2">
                            <i className="feather-map-pin text-primary-light text-md" />
                            <span>{data?.location_space_name}</span>
                        </div>
                        </div>
                    {!is_from_client_modal && (
                        <div className="app-btns-btm">
                            <button
                                type="button"
                                className="bg-primary-lighter bg-opacity-40  items-center flex gap-1"
                                onClick={(e) => {
                                    onClickEdit(e.nativeEvent)
                                }}
                            >
                                <i className="feather-edit text-sm" />
                                <Translation
                                    render_as="span"
                                    content_key={`edit_${data?.availability_id
                                        ? 'class'
                                        : 'appointment'
                                        }`}
                                    translations={translations}
                                />
                            </button>
                            <button
                                type="button"
                                className="bg-red-500 bg-opacity-10  text-red-500 items-center flex gap-1"
                                onClick={() => {
                                    switch (data?.service_type) {
                                        case ServiceApiTypeEnum.GROUP:
                                            handleCancelClass()

                                            break
                                        case ServiceApiTypeEnum.SERIES:
                                            promptDeleteSeries(true)
                                            break
                                        default:
                                            setPromptAppointmentCancel(true)
                                            break
                                    }
                                }}
                            >
                                <i className="feather-trash-2 text-sm" />
                                <Translation
                                    render_as="span"
                                    content_key={
                                        data?.availability_id
                                            ? 'delete_class'
                                            : 'cancel_appointment'
                                    }
                                />
                            </button>
                        </div>
                    )}
                </div>
                    <div className='attendes-editfields'>

                  
                {current_data ? (
                    <GEditItem
                        // disable_date={tempInfo?.recurring}
                        key={`edit_item_${current_data.id}`}
                        item={current_data}
                        onChange={handleEditChange}
                        onCancel={cancelEdit}
                        onSave={async () => {
                            checkRecurrence()
                        }}
                        is_updating={is_updating}
                        is_save_disabled={is_save_disabled}
                    />
                ) : (
                    <></>
                )}
                  </div>

                <div className="bg-primary-100 px-6 py-4 ">
                    <div className=" app-attendes">
                    <div className="flex justify-between items-center">
                        <Translation
                            render_as="div"
                            className="text-lg text-primary font-medium"
                            content_key="attendees"
                        />
                        <div>
                            <small className="font-semibold tracking-wider">
                                {data?.slots?.taken || 0}
                                /{data?.slots?.max || 0}
                            </small>
                        </div>
                    </div>
                    {!is_from_client_modal && (
                        <div className="app-attendes-btns">
                            <button
                                className="btn items-center flex gap-1 text-primary"
                                type="button"
                                onClick={() => {
                                    toggleAddExistingClientWindow(true)
                                }}
                            >
                                <i className="feather-plus text-md" />
                                <Translation
                                    render_as="span"
                                    content_key="add_attendee"
                                    translations={translations}
                                />
                            </button>
                            <Menu
                                as="div"
                                className="relative inline-block text-left hidden"
                            >
                                <Menu.Button className="btn items-center flex gap-2">
                                    <i className="feather-plus text-md" />
                                    <Translation
                                        render_as="span"
                                        content_key="add_attendee"
                                        translations={translations}
                                    />
                                </Menu.Button>
                                <Menu.Items className="absolute right-0 w-56 mt-2 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="px-1 py-1 ">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    className={`${active &&
                                                        'bg-primary-lighter'
                                                        } w-full h-10 text-left px-2`}
                                                    type="button"
                                                    onClick={() => {
                                                        toggleCreateClientWindow(
                                                            true
                                                        )
                                                    }}
                                                >
                                                    New Client
                                                </button>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    className={`${active &&
                                                        'bg-primary-lighter'
                                                        } w-full h-10 text-left px-2`}
                                                    type="button"
                                                    onClick={() => {
                                                        toggleAddExistingClientWindow(
                                                            true
                                                        )
                                                    }}
                                                >
                                                    Existing Client
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Menu>

                            <button
                                type="button"
                                className="btn text-primary items-center flex gap-2"
                                onClick={() => {
                                    if (attendees?.length > 0) {
                                        window
                                            .open(
                                                `mailto:${attendees
                                                    .map(
                                                        (item) =>
                                                            item.user.email
                                                    )
                                                    .join(',')}`,
                                                '__blank'
                                            )
                                            ?.focus()
                                    }
                                }}
                            >
                                <i className="feather-mail text-md" />
                                <Translation
                                    render_as="span"
                                    content_key="email_attendees"
                                    translations={translations}
                                />
                            </button>
                        </div>
                    )}

                    <div className="flex flex-col py-2 gap-y-4">
                        {attendees?.map(
                            (attendee: AttendeeItem, key: number) => (
                                <AttendeeListItem
                                    attendee={attendee}
                                    data={data}
                                    key={key}
                                    expand={(e) => {
                                        if (
                                            !is_from_client_modal ||
                                            key == main_attendee_idx
                                        ) {
                                            if (key == item_to_expand) {
                                                expandBookingAttendeeItem(
                                                    -1
                                                )
                                            } else {
                                                expandBookingAttendeeItem(
                                                    key
                                                )
                                            }
                                        }
                                    }}
                                    is_edit_disabled={is_from_client_modal}
                                    is_expanded={item_to_expand === key}
                                    block_ux={!!queue_cancel_booking}
                                    cancelAppointment={(id) => {
                                        if (data?.appointment_id) {
                                            promptCancelBooking(
                                                data?.appointment_id
                                            )
                                            setSelectedAttendee(
                                                id?.id
                                                    ? id?.id
                                                    : (id as string)
                                            )
                                        } else {
                                            promptCancelBooking(
                                                attendee.booking_id
                                            )
                                        }
                                    }}
                                    notifyAttendeeUpdatedSuccessfully={
                                        notifyAttendeeUpdatedSuccessfully
                                    }
                                    is_disable_others={
                                        is_from_client_modal &&
                                        key != main_attendee_idx
                                    }
                                    has_forms={intake_forms?.length > 0}
                                    // TODO: find a consistent id source for all service types 
                                    handleShowResponses={[attendee?.order?.clientId || "", attendee?.user?.id || "", attendee?.id || ""].some((id) => client_ids_with_responses.includes(id)) ? async () => {
                                        if (attendee.order?.id) { //TODO: extract this to a reusable function
                                            try {
                                                setGlobalLoading(true)
                                                let client_responses: IntakeClientResponse[] = []
                                                let responses: IntakeResponseDTO[] = await getApiRequest(`/v1/intake-forms/responses/by-order-id?orderId=${attendee.order?.id}`)
                                                if (responses?.length > 0) {
                                                    for (const response of responses) {
                                                        let client_response = {
                                                            client: {
                                                                user: {
                                                                    first_name: attendee.user?.firstName || "",
                                                                    last_name: attendee.user?.lastName || "",
                                                                    email: attendee.user?.email || "",
                                                                    phone: attendee.user?.phone || ""
                                                                }
                                                            },
                                                            response: mapIntakeResponseFromDTO(response)
                                                        } as IntakeClientResponse
                                                        client_responses.push(client_response)
                                                    }
                                                }
                                                setClientResponses(client_responses)
                                                toggleShowResponses({ show: true })
                                            } catch (error) {
                                                setGlobalError(error)
                                            } finally {
                                                setGlobalLoading(false)
                                            }
                                        }
                                    } : undefined}
                                />
                            )
                        )}
                    </div>
                    </div>
                </div>
                </div>
            </div>
        </Popup>
    )
}
